import { useMemo } from 'react';
import { AIProvider } from '../../chatModels';
import { useModels } from '../../contexts/ModelContext';
import {
  modelSupportsAudio,
  modelSupportsFiles,
  modelSupportsImages,
  modelSupportsReasoning,
  modelSupportsVideo
} from '../../utils/modelHelpers';

// Provider groupings and helpers
export const providerGroups = {
  'OpenAI': [AIProvider.OPENAI, AIProvider.OPENAI_REASONING_STREAM, AIProvider.OPENAI_VERIFIED, AIProvider.IMAGE_GENERATION],
  'Anthropic': [AIProvider.ANTHROPIC],
  'Google': [AIProvider.GOOGLE],
  'Perplexity': [AIProvider.PERPLEXITY],
  'xAI': [AIProvider.xAI],
  'DeepSeek': [AIProvider.DEEPSEEK, AIProvider.DEEPSEEK_REASONING],
  'Meta': [AIProvider.LLAMA],
  'Auto Selection': [AIProvider.AUTOSELECTION],
};

export const getProviderGroupName = (provider) => {
  for (const groupName in providerGroups) {
    if (providerGroups[groupName].includes(provider)) {
      return groupName;
    }
  }
  return 'Other';
};

const getBadgeLabel = (model = {}) => {
  if (model.badge && model.badge.toLowerCase() !== 'beta') return model.badge;
  const subtitle = (model.subtitle || '').toLowerCase();
  const name = (model.name || '').toLowerCase();

  if (model.status === 'beta') return null;
  if (model.isNew) return 'New';
  if (subtitle.includes('preview') || name.includes('preview')) return 'Preview';
  if (subtitle.includes('experimental')) return 'Experimental';
  if (subtitle.includes('beta')) return null;

  return null;
};

export const useModelData = (value) => {
  const { models } = useModels();

  const modelOptions = useMemo(() => {
    return (models || [])
      .filter(model => model && model.provider)
      .map((model) => {
        const displayProvider = model.displayProvider || model.provider;
        const capabilities = model.capabilities || {
          images: modelSupportsImages(model),
          files: modelSupportsFiles(model),
          audio: modelSupportsAudio(model),
          video: modelSupportsVideo(model),
          reasoning: modelSupportsReasoning(model),
        };
        return {
          display: model.provider === AIProvider.AUTOSELECTION ? 'Auto' : model.name,
          value: model.modelString,
          provider: displayProvider,
          routingProvider: model.provider,
          sortOrder: model.sortOrder,
          isPremium: model.isPremium,
          subtitle: model.subtitle,
          description: model.description,
          color: model.color,
          symbol: model.symbol,
          isNew: model.isNew,
          isOld: model.isOld,
          badge: getBadgeLabel(model),
          isAuto: model.provider === AIProvider.AUTOSELECTION,
          capabilities
        };
      });
  }, [models]);

  const providers = useMemo(() => {
    const groupBuckets = {};
    modelOptions.forEach((model) => {
      const groupName = getProviderGroupName(model.provider);
      if (!groupBuckets[groupName]) {
        groupBuckets[groupName] = {
          key: groupName,
          label: groupName,
          color: model.color,
          count: 0,
        };
      }
      groupBuckets[groupName].count += 1;
      if (!groupBuckets[groupName].color && model.color) {
        groupBuckets[groupName].color = model.color;
      }
    });

    // Preserve defined providerGroups order, then append any "Other"
    const ordered = Object.keys(providerGroups)
      .filter((name) => groupBuckets[name])
      .map((name) => groupBuckets[name]);

    if (groupBuckets.Other) ordered.push(groupBuckets.Other);
    return ordered;
  }, [modelOptions]);

  const placeholderOption = {
    display: 'Auto',
    value: '',
    provider: '',
    sortOrder: Number.MAX_SAFE_INTEGER,
    isPremium: false,
    subtitle: '',
    color: '#808080',
    symbol: 'sparkles',
    isNew: false,
    isOld: false,
    badge: null,
    isAuto: true,
    capabilities: {}
  };

  const getSelectedModel = () => {
    return modelOptions.find((opt) => opt.value === value) || modelOptions[0] || placeholderOption;
  };

  return {
    modelOptions,
    providers,
    getSelectedModel
  };
};
